// Icon utilities for ArchiMate entity types and relationship types
import { TYPE_PROFILE } from "./typeProfile";

/**
 * Get the emoji icon for an entity type by its icon name
 */
export const getEntityTypeIcon = (iconName) => {
  if (!iconName) return "📦"; // Default icon

  const icon = ARCHIMATE_ICONS.find((i) => i.name === iconName);
  return icon?.emoji || "📦";
};

// Get emoji directly by entity type value using unified profile
export const getEntityEmojiByType = (entityTypeValue) => {
  const prof = TYPE_PROFILE[entityTypeValue];
  return prof?.emoji || "📦";
};

/**
 * ArchiMate icons mapping
 * Maps icon names (as stored in database) to emoji icons
 */
const ARCHIMATE_ICONS = [
  // Strategic Layer Icons
  { name: "goal", emoji: "🎯", label: "Goal" },
  { name: "business-context", emoji: "🏛️", label: "Business Context" },
  { name: "value-chain", emoji: "⛓️", label: "Value Chain" },
  { name: "idea", emoji: "💡", label: "Idea" },
  { name: "initiative", emoji: "🚀", label: "Initiative" },
  { name: "program", emoji: "📋", label: "Program" },
  { name: "project", emoji: "📊", label: "Project" },
  { name: "task", emoji: "✅", label: "Task" },
  { name: "strategy", emoji: "🧭", label: "Strategy" },
  { name: "vision", emoji: "👁️", label: "Vision" },
  { name: "mission", emoji: "🎖️", label: "Mission" },
  { name: "objective", emoji: "🎯", label: "Objective" },
  { name: "outcome", emoji: "📈", label: "Outcome" },
  { name: "deliverable", emoji: "📦", label: "Deliverable" },

  // Business Layer Icons
  { name: "business-service", emoji: "🏢", label: "Business Service" },
  { name: "business-process", emoji: "⚙️", label: "Business Process" },
  { name: "business-function", emoji: "📋", label: "Business Function" },
  { name: "business-role", emoji: "👤", label: "Business Role" },
  { name: "business-actor", emoji: "👥", label: "Business Actor" },
  { name: "business-object", emoji: "📄", label: "Business Object" },
  { name: "business-event", emoji: "📅", label: "Business Event" },
  { name: "business-interface", emoji: "🔗", label: "Business Interface" },
  { name: "business-capability", emoji: "💪", label: "Business Capability" },
  { name: "business-value", emoji: "💰", label: "Business Value" },
  { name: "stakeholder", emoji: "🤝", label: "Stakeholder" },
  { name: "organization", emoji: "🏢", label: "Organization" },
  { name: "supplier", emoji: "🚚", label: "Supplier" },
  { name: "department", emoji: "🏬", label: "Department" },
  { name: "team", emoji: "👥", label: "Team" },
  { name: "business-context", emoji: "🏛️", label: "Business Context" },

  // Application Layer Icons
  { name: "application-service", emoji: "🔧", label: "Application Service" },
  {
    name: "application-component",
    emoji: "💻",
    label: "Application Component",
  },
  { name: "application-function", emoji: "⚡", label: "Application Function" },
  {
    name: "application-interface",
    emoji: "🔌",
    label: "Application Interface",
  },
  {
    name: "application-collaboration",
    emoji: "🤝",
    label: "Application Collaboration",
  },
  { name: "application", emoji: "💻", label: "Application" },
  { name: "interface", emoji: "🔌", label: "Interface" },
  { name: "data-object", emoji: "💾", label: "Data Object" },
  { name: "data", emoji: "💾", label: "Data" },
  { name: "software-component", emoji: "💿", label: "Software Component" },
  { name: "microservice", emoji: "🔧", label: "Microservice" },
  { name: "api-gateway", emoji: "🚪", label: "API Gateway" },
  { name: "web-service", emoji: "🌐", label: "Web Service" },

  // Technology Layer Icons
  { name: "technology-service", emoji: "🖥️", label: "Technology Service" },
  { name: "technology-function", emoji: "⚙️", label: "Technology Function" },
  { name: "technology-component", emoji: "🔧", label: "Technology Component" },
  { name: "technology-interface", emoji: "🔌", label: "Technology Interface" },
  {
    name: "technology-collaboration",
    emoji: "🌐",
    label: "Technology Collaboration",
  },
  { name: "artifact", emoji: "📁", label: "Artifact" },
  { name: "node", emoji: "🖥️", label: "Node" },
  { name: "device", emoji: "📱", label: "Device" },
  { name: "network", emoji: "🌐", label: "Network" },
  { name: "path", emoji: "🛤️", label: "Path" },
  { name: "communication-path", emoji: "📡", label: "Communication Path" },
  {
    name: "infrastructure-service",
    emoji: "🏗️",
    label: "Infrastructure Service",
  },
  { name: "platform", emoji: "🏗️", label: "Platform" },
  { name: "middleware", emoji: "🔗", label: "Middleware" },
  { name: "provider", emoji: "🤝", label: "Provider" },

  // Infrastructure Icons
  { name: "server", emoji: "🖥️", label: "Server" },
  { name: "database", emoji: "🗄️", label: "Database" },
  { name: "cloud", emoji: "☁️", label: "Cloud" },
  { name: "security", emoji: "🔒", label: "Security" },
  { name: "api", emoji: "🔌", label: "API" },
  { name: "workflow", emoji: "🔄", label: "Workflow" },
  { name: "container", emoji: "📦", label: "Container" },
  { name: "kubernetes", emoji: "☸️", label: "Kubernetes" },
  { name: "load-balancer", emoji: "⚖️", label: "Load Balancer" },
  { name: "firewall", emoji: "🔥", label: "Firewall" },
  { name: "monitoring", emoji: "📊", label: "Monitoring" },
  { name: "logging", emoji: "📝", label: "Logging" },

  // Additional Business Concepts
  { name: "risk", emoji: "⚠️", label: "Risk" },
  { name: "constraint", emoji: "🚫", label: "Constraint" },
  { name: "requirement", emoji: "📋", label: "Requirement" },
  { name: "policy", emoji: "📜", label: "Policy" },
  { name: "principle", emoji: "📐", label: "Principle" },
  { name: "standard", emoji: "📏", label: "Standard" },
  { name: "guideline", emoji: "📖", label: "Guideline" },
  { name: "procedure", emoji: "📝", label: "Procedure" },
  { name: "rule", emoji: "📐", label: "Rule" },
  { name: "regulation", emoji: "⚖️", label: "Regulation" },

  // Generic/Utility Icons
  { name: "generic", emoji: "📄", label: "Generic" },
  { name: "unknown", emoji: "❓", label: "Unknown" },
  { name: "placeholder", emoji: "🔲", label: "Placeholder" },
  { name: "template", emoji: "📋", label: "Template" },
  { name: "reference", emoji: "📚", label: "Reference" },
  { name: "document", emoji: "📄", label: "Document" },
  { name: "file", emoji: "📁", label: "File" },
  { name: "folder", emoji: "📂", label: "Folder" },
];

